var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/tooltip.ts
var ViewportMargin = 8;
var ArrowInset = 10;
function clampTooltipX(targetCenterX, bubbleWidth, viewportWidth, margin = ViewportMargin) {
  const half = bubbleWidth / 2;
  const min = margin + half;
  const max = viewportWidth - margin - half;
  const centerX = max < min ? viewportWidth / 2 : Math.min(Math.max(targetCenterX, min), max);
  const rawArrowX = targetCenterX - (centerX - half);
  const arrowX = Math.min(Math.max(rawArrowX, ArrowInset), Math.max(bubbleWidth - ArrowInset, 0));
  return {
    centerX,
    arrowX
  };
}
__name(clampTooltipX, "clampTooltipX");
function clampTooltipLeft(left, bubbleWidth, viewportWidth, margin = ViewportMargin) {
  const max = viewportWidth - margin - bubbleWidth;
  return max < margin ? margin : Math.min(Math.max(left, margin), max);
}
__name(clampTooltipLeft, "clampTooltipLeft");
var installed = false;
function setupTooltip() {
  if (installed || typeof document === "undefined") {
    return;
  }
  installed = true;
  const TooltipClass = "ds-tooltip";
  const LnbSelector = ".ds-lnb--collapsed .ds-lnb__menu";
  const TopSelector = ".icon-btn, .ds-btn--icon, .org-tree__action-btn, [data-ds-tip='top']";
  const CompactSelector = "select, [data-ds-tip='compact'], .ms-dropdown__trigger";
  const CompactCenterSelector = "[data-ds-tip='compact-center']";
  const CompactGap = 4;
  const TruncSelector = ".role-card__group-label, .role-card__pill, .cell-trunc-tip";
  const MultilineSelector = "[title], [data-tip-title]";
  const Gap = 4;
  const ArrowDepth = 6;
  const LnbGap = Gap + ArrowDepth;
  const TopGap = Gap + ArrowDepth;
  let tip = null;
  let tipText = null;
  let active = null;
  function ensureTip() {
    if (tip) {
      return tip;
    }
    tip = document.createElement("div");
    tip.className = TooltipClass;
    tip.setAttribute("role", "tooltip");
    tip.setAttribute("aria-hidden", "true");
    tipText = document.createTextNode("");
    tip.appendChild(tipText);
    document.body.appendChild(tip);
    return tip;
  }
  __name(ensureTip, "ensureTip");
  function targetMatch(el) {
    const lnb = el.closest(LnbSelector);
    if (lnb instanceof HTMLElement) {
      return {
        node: lnb,
        placement: "right"
      };
    }
    const compactCenter = el.closest(CompactCenterSelector);
    if (compactCenter instanceof HTMLElement) {
      return {
        node: compactCenter,
        placement: "compact-center"
      };
    }
    const compact = el.closest(CompactSelector);
    if (compact instanceof HTMLElement && !(compact.tagName === "SELECT" && hasMultilineLabel(compact))) {
      return {
        node: compact,
        placement: "compact"
      };
    }
    const btn = el.closest(TopSelector);
    if (btn instanceof HTMLElement) {
      return {
        node: btn,
        placement: "top"
      };
    }
    const trunc = el.closest(TruncSelector);
    if (trunc instanceof HTMLElement) {
      return {
        node: trunc,
        placement: "top",
        truncateOnly: true
      };
    }
    const multiline = el.closest(MultilineSelector);
    if (multiline instanceof HTMLElement && hasMultilineLabel(multiline)) {
      return {
        node: multiline,
        placement: "top"
      };
    }
    return null;
  }
  __name(targetMatch, "targetMatch");
  function hasMultilineLabel(node) {
    const label = node.getAttribute("title") ?? node.dataset.tipTitle ?? "";
    return label.includes("\n");
  }
  __name(hasMultilineLabel, "hasMultilineLabel");
  function applyCenteredX(el, r, viewportWidth) {
    const { centerX, arrowX } = clampTooltipX(r.left + r.width / 2, el.offsetWidth, viewportWidth);
    el.style.left = `${centerX}px`;
    if (Math.abs(centerX - (r.left + r.width / 2)) > 0.5) {
      el.style.setProperty("--ds-tip-arrow-x", `${arrowX}px`);
    }
  }
  __name(applyCenteredX, "applyCenteredX");
  function show(node, placement) {
    let label = node.getAttribute("title") || node.dataset.tipTitle || "";
    if (placement === "compact") {
      if (node instanceof HTMLSelectElement) {
        const opt = node.options[node.selectedIndex];
        if (opt?.text) {
          label = opt.text;
        }
      } else if (node.classList.contains("ms-dropdown__trigger")) {
        const container = node.closest(".ms-dropdown");
        const checked = [];
        if (container) {
          container.querySelectorAll(".ms-dropdown__item").forEach((item) => {
            const cb = item.querySelector('input[type="checkbox"]');
            if (cb instanceof HTMLInputElement && cb.checked) {
              const txt = item.querySelector(".ms-dropdown__item-label")?.textContent?.trim();
              if (txt) {
                checked.push(txt);
              }
            }
          });
        }
        if (checked.length > 1) {
          label = checked.join(", ");
        } else {
          const labelEl = node.querySelector(".ms-dropdown__label");
          const txt = labelEl?.textContent?.trim();
          if (txt) {
            label = txt;
          }
        }
      }
    }
    if (!label) {
      return;
    }
    const el = ensureTip();
    if (tipText) {
      tipText.data = label;
    } else {
      el.textContent = label;
    }
    el.classList.toggle("ds-tooltip--multiline", label.includes("\n"));
    el.classList.toggle("ds-tooltip--light", node.closest('[data-ds-tip-tone="light"]') !== null);
    el.setAttribute("data-placement", placement);
    el.removeAttribute("data-flip");
    el.style.removeProperty("--ds-tip-arrow-x");
    const r = node.getBoundingClientRect();
    const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
    if (placement === "right") {
      const lnb = node.closest(".ds-lnb--collapsed");
      const x = (lnb ? lnb.getBoundingClientRect().right : r.right) + LnbGap;
      el.style.left = `${x}px`;
      el.style.top = `${r.top + r.height / 2}px`;
    } else if (placement === "compact") {
      el.style.left = `${clampTooltipLeft(r.left, el.offsetWidth, viewportWidth)}px`;
      if (r.top - CompactGap - el.offsetHeight < 0) {
        el.setAttribute("data-flip", "down");
        el.style.top = `${r.bottom + CompactGap}px`;
      } else {
        el.style.top = `${r.top - CompactGap}px`;
      }
    } else if (placement === "compact-center") {
      applyCenteredX(el, r, viewportWidth);
      if (r.top - TopGap - el.offsetHeight < 0) {
        el.setAttribute("data-flip", "down");
        el.style.top = `${r.bottom + TopGap}px`;
      } else {
        el.style.top = `${r.top - TopGap}px`;
      }
    } else {
      applyCenteredX(el, r, viewportWidth);
      if (r.top - TopGap - el.offsetHeight < 0) {
        el.setAttribute("data-flip", "down");
        el.style.top = `${r.bottom + TopGap}px`;
      } else {
        el.style.top = `${r.top - TopGap}px`;
      }
    }
    requestAnimationFrame(() => el.classList.add("is-visible"));
  }
  __name(show, "show");
  function hide() {
    if (tip) {
      tip.classList.remove("is-visible");
    }
    if (active?.dataset.tipTitle) {
      if (!active.hasAttribute("title")) {
        active.setAttribute("title", active.dataset.tipTitle);
      }
      delete active.dataset.tipTitle;
    }
    active = null;
  }
  __name(hide, "hide");
  const ClickTriggerSelector = "[data-ds-tip-trigger='click']";
  function activate(match) {
    const title = match.node.getAttribute("title");
    if (title) {
      match.node.dataset.tipTitle = title;
      match.node.setAttribute("aria-label", title);
      match.node.removeAttribute("title");
    } else if (!match.node.dataset.tipTitle) {
      return;
    }
    active = match.node;
    show(match.node, match.placement);
  }
  __name(activate, "activate");
  document.addEventListener("pointerover", (e) => {
    const t = e.target;
    if (!t) {
      return;
    }
    if (t.closest(ClickTriggerSelector)) {
      if (active && !active.matches(ClickTriggerSelector)) {
        hide();
      }
      return;
    }
    const match = targetMatch(t);
    if (match && match.node !== active) {
      hide();
      if (match.truncateOnly && match.node.scrollWidth <= match.node.clientWidth + 1 && match.node.scrollHeight <= match.node.clientHeight + 1) {
        return;
      }
      const title = match.node.getAttribute("title");
      if (title) {
        match.node.dataset.tipTitle = title;
        match.node.setAttribute("aria-label", title);
        match.node.removeAttribute("title");
        active = match.node;
        show(match.node, match.placement);
      } else if (match.node.dataset.tipTitle) {
        active = match.node;
        show(match.node, match.placement);
      } else if (match.placement === "compact" && (match.node instanceof HTMLSelectElement || match.node.classList.contains("ms-dropdown__trigger"))) {
        active = match.node;
        show(match.node, match.placement);
      }
    } else if (!match && active) {
      hide();
    }
  }, true);
  document.addEventListener("pointerleave", () => hide(), true);
  document.addEventListener("click", (e) => {
    const t = e.target;
    const trigger = t?.closest(ClickTriggerSelector);
    if (trigger instanceof HTMLElement) {
      const match = targetMatch(trigger);
      if (match && match.node === trigger) {
        if (active === trigger) {
          hide();
          return;
        }
        hide();
        activate(match);
        return;
      }
    }
    hide();
  });
}
__name(setupTooltip, "setupTooltip");
export {
  clampTooltipLeft,
  clampTooltipX,
  setupTooltip
};
//# sourceMappingURL=tooltip.js.map